import type { Coordinate } from 'ol/coordinate';
import type { Ref } from 'vue';
import { ref } from 'vue';

import type { OperationResultDTO, OperationTrackerDetailsDTO, UserResultDTO } from '@nora-monorepo/nora-api-client';
import type { LatitudeLongitude, NoraCoordinateFormat } from '@nora-monorepo/shared';
import { isNonEmptyString } from '@nora-monorepo/shared';
import { LatLong, useConfigStore } from '@nora-monorepo/ui-shared';

import type { MapConfig, MapConfigOverlayMap } from '#nora-ui/map/types/map.types';
import { useOperationStore } from '#nora-ui/stores/operation-store';

/**
 * Helper class to access and modify NoraMap configuration properties in a reactive way.
 * Provides getters and setters for various map configuration options.
 */
export class NoraMapRef {
  private operationStore = useOperationStore();

  /**
   * Reactive loading state for map data operations.
   * True when reloadMapData() or an operation show/hide is in progress.
   */
  readonly isLoadingRef = ref(false);

  /**
   * Current mouse coordinates on the map, updated on pointer move.
   * Useful for paste operations and other mouse-position-dependent features.
   */
  readonly mouseCoordinates = ref<Coordinate | null>(null);

  /**
   * The unique identifier of the map.
   */
  get id(): string {
    return this.mapConfigRef.value.id;
  }

  /**
   * Get the current loading state of the map.
   * Returns true when reloadMapData() or an operation show/hide is in progress.
   */
  get isLoading(): boolean {
    return this.isLoadingRef.value;
  }

  /**
   * The ids of every operation on this map, regardless of visibility.
   */
  get operationIds(): string[] {
    return this.mapConfigRef.value.operations.map(operation => operation.id);
  }

  /**
   * The ids of the operations currently visible (eye toggled on) on this map.
   */
  get visibleOperationIds(): string[] {
    return this.mapConfigRef.value.operations
      .filter(operation => operation.visible)
      .map(operation => operation.id);
  }

  /**
   * The resolved details of every operation on this map (unresolved ids are skipped).
   */
  get operations(): OperationResultDTO[] {
    return this.operationIds
      .map(operationId => this.operationStore.getOperation(operationId))
      .filter((operation): operation is OperationResultDTO => operation !== undefined);
  }

  /**
   * The resolved details of the operations currently visible on this map.
   */
  get visibleOperations(): OperationResultDTO[] {
    return this.visibleOperationIds
      .map(operationId => this.operationStore.getOperation(operationId))
      .filter((operation): operation is OperationResultDTO => operation !== undefined);
  }

  /**
   * The operation that newly drawn features are saved to (the draw target), if any.
   */
  get activeOperationId(): string | undefined {
    return this.mapConfigRef.value.activeOperationId;
  }

  /**
   * The resolved details of the active draw-target operation, if any.
   */
  get activeOperation(): OperationResultDTO | undefined {
    if (!isNonEmptyString(this.activeOperationId)) {
      return undefined;
    }

    return this.operationStore.getOperation(this.activeOperationId);
  }

  /**
   * Whether the given operation is currently visible (eye toggled on) on this map.
   */
  isOperationVisible(operationId: string): boolean {
    return this.mapConfigRef.value.operations.some(
      operation => operation.id === operationId && operation.visible,
    );
  }

  /**
   * Whether the given operation is present on this map (regardless of visibility).
   */
  hasOperation(operationId: string): boolean {
    return this.mapConfigRef.value.operations.some(operation => operation.id === operationId);
  }

  /**
   * The resolved details of a single operation on this map, if present and loaded.
   */
  getOperation(operationId: string): OperationResultDTO | undefined {
    if (!this.hasOperation(operationId)) {
      return undefined;
    }

    return this.operationStore.getOperation(operationId);
  }

  /**
   * The users of a single operation.
   */
  operationUsers(operationId: string): UserResultDTO[] {
    return this.getOperation(operationId)?.users?.map(({ user }) => user) ?? [];
  }

  /**
   * The user ids of a single operation.
   */
  operationUserIds(operationId: string): string[] {
    return this.operationUsers(operationId).map(user => user.id);
  }

  /**
   * The users of a single operation that have an active location.
   */
  operationUsersWithLastKnownTrack(operationId: string): UserResultDTO[] {
    return this.operationUsers(operationId).filter(user => user.lastTrack);
  }

  /**
   * The trackers of a single operation.
   */
  operationTrackers(operationId: string): OperationTrackerDetailsDTO[] {
    return this.getOperation(operationId)?.trackers ?? [];
  }

  /**
   * The tracker ids of a single operation.
   */
  operationTrackerIds(operationId: string): string[] {
    return this.operationTrackers(operationId).map(tracker => tracker.id);
  }

  /**
   * The trackers of a single operation that have an active location.
   */
  operationTrackersWithLastKnownTrack(operationId: string): OperationTrackerDetailsDTO[] {
    return this.operationTrackers(operationId).filter(tracker => tracker.lastTrack);
  }

  /**
   * The current zoom level of the map.
   */
  get zoom(): number {
    return this.mapConfigRef.value.zoom;
  }

  /**
   * The current center coordinates of the map.
   */
  get center(): LatLong {
    return new LatLong(this.centerRaw.latitude, this.centerRaw.longitude);
  }

  get centerRaw(): LatitudeLongitude {
    return this.mapConfigRef.value.center;
  }

  /**
   * The current rotation of the map in radians.
   */
  get rotation(): number {
    return this.mapConfigRef.value.rotation;
  }

  /**
   * The current map projection.
   */
  get projection(): string {
    return this.mapConfigRef.value.projection;
  }

  /**
   * The base map identifier, if any.
   */
  get baseMap(): string | undefined {
    return this.mapConfigRef.value.baseMap;
  }

  /**
   * The list of overlay maps.
   */
  get overlayMaps(): MapConfigOverlayMap[] {
    return this.mapConfigRef.value.overlayMaps;
  }

  /**
   * The list of offline maps.
   */
  get offlineMaps(): MapConfigOverlayMap[] {
    return this.mapConfigRef.value.offlineMaps;
  }

  /**
   * Sets the list of offline maps.
   * @param maps - The new list of offline maps.
   */
  set offlineMaps(maps: MapConfigOverlayMap[]) {
    this.mapConfigRef.value.offlineMaps = maps;
  }

  /**
   * The active draw layer identifier, if any.
   */
  get activeDrawLayerId(): string | undefined {
    return this.mapConfigRef.value.activeDrawLayerId;
  }

  /**
   * The identifier of the producer being followed, if any.
   */
  get followedProducerId(): string | undefined {
    return this.mapConfigRef.value.followedProducerId;
  }

  /**
   * Indicates whether heading-up mode is enabled (map rotates to match user heading).
   */
  get isInHeadingUpMode(): boolean {
    return this.mapConfigRef.value.headingUpMode ?? false;
  }

  /**
   * Indicates whether the scale line is visible on the map.
   */
  get isScaleLineVisible(): boolean {
    const configStore = useConfigStore();
    return configStore.getUserMapSettingsRef().value.showScaleLine;
  }

  /**
   * Sets the visibility of the scale line on the map.
   * @param value - True to show the scale line, false to hide it.
   */
  set isScaleLineVisible(value: boolean) {
    const configStore = useConfigStore();
    const mapSettings = configStore.getUserMapSettingsRef().value;
    mapSettings.showScaleLine = value;
    void configStore.updateUserMapDisplaySettings(mapSettings.toDTO());
  }

  /**
   * The list of visible coordinate systems on the map.
   */
  get visibleCoordinateFormats(): NoraCoordinateFormat[] {
    const configStore = useConfigStore();
    return configStore.getUserMapSettingsRef().value.visibleCoordinateFormats;
  }

  /**
   * Constructor for NoraMapRef.
   * @param mapConfigRef
   */
  constructor(private mapConfigRef: Ref<MapConfig>) {}

  /**
   * Get the raw map configuration object.
   * Useful for persistence and serialization.
   */
  getMapConfig(): MapConfig {
    return this.mapConfigRef.value;
  }

  /**
   * Toggles the visibility of the scale line on the map.
   */
  toggleScaleLineVisibility(): void {
    const configStore = useConfigStore();
    const mapSettings = configStore.getUserMapSettingsRef().value;
    mapSettings.toggleScaleLineVisibility();
    void configStore.updateUserMapDisplaySettings(mapSettings.toDTO());
  }

  /**
   * Checks if there are any visible coordinate systems.
   * @returns True if there are visible coordinate systems, false otherwise.
   */
  hasVisibleCoordinateSystems(): boolean {
    const configStore = useConfigStore();
    return configStore.getUserMapSettingsRef().value.visibleCoordinateFormats.length > 0;
  }

  /**
   * Checks if a specific coordinate format is visible.
   * @param coordinateFormat - The coordinate system to check.
   * @returns True if the coordinate system is visible, false otherwise.
   */
  isCoordinateFormatVisible(coordinateFormat: NoraCoordinateFormat): boolean {
    const configStore = useConfigStore();
    return configStore.getUserMapSettingsRef().value.isCoordinateFormatEnabled(coordinateFormat);
  }

  /**
   * Toggles the visibility of a specific coordinate system.
   * @param coordinateFormat - The coordinate format to toggle.
   */
  toggleCoordinateFormat(coordinateFormat: NoraCoordinateFormat): void {
    const configStore = useConfigStore();
    const mapSettings = configStore.getUserMapSettingsRef().value;
    mapSettings.toggleCoordinateFormat(coordinateFormat);
    void configStore.updateUserMapDisplaySettings(mapSettings.toDTO());
  }

  /**
   * Sets the identifier of the producerId to follow on the map.
   * @param producerId
   */
  setFollowedProducerId(producerId: string | undefined): void {
    this.mapConfigRef.value.followedProducerId = producerId;
  }

  /**
   * Stops following any producer on the map.
   */
  stopFollowing(): void {
    this.setFollowedProducerId(undefined);
  }

  /**
   * Sets whether the map should rotate to match the current user's heading.
   * @param enabled - True to enable heading-up mode, false to disable.
   */
  setHeadingUpMode(enabled: boolean): void {
    this.mapConfigRef.value.headingUpMode = enabled;
  }

  /**
   * Sets the visibility of an overlay map.
   * @param overlayMapId - The identifier of the overlay map.
   * @param visible - True to make the overlay map visible, false to hide it.
   */
  setOverlayMapVisibility(overlayMapId: string, visible: boolean): void {
    const overlayMaps = this.mapConfigRef.value.overlayMaps;
    const overlayMapIndex = overlayMaps.findIndex(map => map.id === overlayMapId);

    if (overlayMapIndex === -1) {
      throw new Error(`Overlay map with id ${overlayMapId} not found`);
    }

    const overlayMap = overlayMaps[overlayMapIndex];

    const updatedOverlayMap = {
      ...overlayMap,
      visible,
    };

    this.mapConfigRef.value.overlayMaps = [
      ...overlayMaps.slice(0, overlayMapIndex),
      updatedOverlayMap,
      ...overlayMaps.slice(overlayMapIndex + 1),
    ].filter((map): map is MapConfigOverlayMap => Boolean(map.id));
  }

  /**
   * Gets the visibility of an overlay map.
   * @param overlayMapId - The identifier of the overlay map.
   * @returns True if the overlay map is visible, false otherwise.
   */
  getOverlayMapVisibility(overlayMapId: string): boolean {
    const overlayMaps = this.mapConfigRef.value.overlayMaps;
    const overlayMap = overlayMaps.find(map => map.id === overlayMapId);

    if (!overlayMap) {
      throw new Error(`Overlay map with id ${overlayMapId} not found`);
    }

    return overlayMap.visible;
  }

  /**
   * Toggles the visibility of an offline map.
   * @param offlineMapId - The identifier of the offline map to toggle.
   */
  toggleOfflineMapVisibility(offlineMapId: string): void {
    this.setOfflineMapVisibility(
      offlineMapId,
      !this.getOfflineMapVisibility(offlineMapId),
    );
  }

  /**
   * Sets the visibility of an offline map.
   * @param offlineMapId - The identifier of the offline map.
   * @param visible - True to make the offline map visible, false to hide it.
   */
  setOfflineMapVisibility(offlineMapId: string, visible: boolean): void {
    const offlineMaps = this.mapConfigRef.value.offlineMaps;
    const offlineMapIndex = offlineMaps.findIndex(map => map.id === offlineMapId);

    if (offlineMapIndex === -1) {
      throw new Error(`Offline map with id ${offlineMapId} not found`);
    }

    const offlineMap = offlineMaps[offlineMapIndex];

    const updatedOfflineMap = {
      ...offlineMap,
      visible,
    };

    this.mapConfigRef.value.offlineMaps = [
      ...offlineMaps.slice(0, offlineMapIndex),
      updatedOfflineMap,
      ...offlineMaps.slice(offlineMapIndex + 1),
    ].filter((map): map is MapConfigOverlayMap => Boolean(map.id));
  }

  /**
   * Gets the visibility of an offline map.
   * @param offlineMapId - The identifier of the offline map.
   * @returns True if the offline map is visible, false otherwise.
   */
  getOfflineMapVisibility(offlineMapId: string): boolean {
    const offlineMaps = this.mapConfigRef.value.offlineMaps;
    const offlineMap = offlineMaps.find(map => map.id === offlineMapId);

    if (!offlineMap) {
      throw new Error(`Offline map with id ${offlineMapId} not found`);
    }

    return offlineMap.visible;
  }

  /**
   * Sets the active draw layer.
   * @param drawLayerId - The identifier of the draw layer to set as active.
   */
  setActiveDrawLayer(drawLayerId: string | undefined): void {
    this.mapConfigRef.value.activeDrawLayerId = drawLayerId;
  }

  /**
   * Clears the active draw layer.
   */
  clearActiveDrawLayer(): void {
    this.mapConfigRef.value.activeDrawLayerId = undefined;
  }

  /**
   * Adds an operation to the map if not already present. New operations are visible by default.
   * @param operationId - The identifier of the operation to add.
   */
  addOperation(operationId: string): void {
    if (this.hasOperation(operationId)) {
      return;
    }

    this.mapConfigRef.value.operations = [
      ...this.mapConfigRef.value.operations,
      { id: operationId, visible: true },
    ];
  }

  /**
   * Removes an operation from the map entirely.
   * @param operationId - The identifier of the operation to remove.
   */
  removeOperation(operationId: string): void {
    this.mapConfigRef.value.operations = this.mapConfigRef.value.operations.filter(
      operation => operation.id !== operationId,
    );
  }

  /**
   * Sets the visibility (eye toggle) of an operation on the map.
   * @param operationId - The identifier of the operation.
   * @param visible - True to show the operation's data, false to hide it.
   */
  setOperationVisibility(operationId: string, visible: boolean): void {
    this.mapConfigRef.value.operations = this.mapConfigRef.value.operations.map(operation =>
      operation.id === operationId ? { ...operation, visible } : operation,
    );
  }

  /**
   * Sets the active draw-target operation.
   * @param operationId - The identifier of the operation to draw into.
   */
  setActiveOperation(operationId: string | undefined): void {
    this.mapConfigRef.value.activeOperationId = operationId;
  }
}
